import Phaser from "phaser";
import { FISH_FRENZY_DURATION_MS, FISH_FRENZY_MULTIPLIER, REFERENCE_HEIGHT, REFERENCE_WIDTH, SPEED_BOOST_DURATION_MS, SPEED_BOOST_MULTIPLIER, TARGET_FISH_COUNT, WORLD_HEIGHT, WORLD_WIDTH } from "../game/constants";
import { PowerUpType, SeasonType, WeaponType } from "../data/enums";
import { GameplayLaunchData, getLakeByKey, LakeConfig } from "../data/lakeData";
import { FishSpawner } from "../entities/fish/FishSpawner";
import { BoatController } from "../entities/player/BoatController";
import { FishBase } from "../entities/fish/FishBase";
import { PowerUpSpawner } from "../entities/powerups/PowerUpSpawner";
import { PowerUpBase } from "../entities/powerups/PowerUpBase";
import { WeaponSystem } from "../entities/player/WeaponSystem";
import { GasTank } from "../entities/player/GasTank";
import { AudioManager } from "../systems/AudioManager";
import { MiniMap } from "../systems/MiniMap";
import { SeasonManager } from "../systems/SeasonManager";
import { ScorePopup } from "../ui/ScorePopup";
import { VirtualDPad } from "../ui/VirtualDPad";

export class GameplayScene extends Phaser.Scene {
    private boat!: BoatController;
    private fishGroup!: Phaser.GameObjects.Group;
    private powerUpGroup!: Phaser.GameObjects.Group;
    private weaponSystem!: WeaponSystem;
    private fishSpawner!: FishSpawner;
    private powerUpSpawner!: PowerUpSpawner;
    private gasTank = new GasTank();

    private score = 0;
    private fishCaught = 0;
    private speedBoostUntil = 0;
    private fishFrenzyUntil = 0;

    private scoreText!: Phaser.GameObjects.Text;
    private fishCountText!: Phaser.GameObjects.Text;
    private gasText!: Phaser.GameObjects.Text;
    private powerUpText!: Phaser.GameObjects.Text;
    private lakeInfoText!: Phaser.GameObjects.Text;
    private fpsText!: Phaser.GameObjects.Text;
    private dPad?: VirtualDPad;
    private currentLake!: LakeConfig;
    private currentSeason: SeasonType = SeasonType.Summer;
    private seasonManager!: SeasonManager;
    private audioManager!: AudioManager;
    private miniMap!: MiniMap;
    private fpsSampleTimer = 0;
    private fpsAccumulated = 0;
    private fpsFrames = 0;
    private runEnded = false;

    constructor() {
        super("GameplayScene");
    }

    create(data?: GameplayLaunchData): void {
        this.score = 0;
        this.fishCaught = 0;
        this.speedBoostUntil = 0;
        this.fishFrenzyUntil = 0;
        this.fpsSampleTimer = 0;
        this.fpsAccumulated = 0;
        this.fpsFrames = 0;
        this.runEnded = false;

        this.createTextures();
        this.currentLake = getLakeByKey(data?.lakeKey ?? "lake-mendota");
        this.currentSeason = data?.season ?? SeasonType.Summer;
        this.audioManager = new AudioManager(this);

        this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
        this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

        const water = this.add.rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, WORLD_WIDTH, WORLD_HEIGHT, this.currentLake.baseWaterColor).setDepth(-20);
        this.seasonManager = new SeasonManager(this);
        this.seasonManager.applySeason(this.currentSeason, water, WORLD_WIDTH, WORLD_HEIGHT);

        for (let i = 0; i < 120; i += 1) {
            this.add.circle(Phaser.Math.Between(0, WORLD_WIDTH), Phaser.Math.Between(0, WORLD_HEIGHT), Phaser.Math.Between(2, 5), 0x2f7ea8, 0.18).setDepth(-19);
        }

        const enableTouchControls = this.sys.game.device.input.touch;
        if (enableTouchControls) {
            this.dPad = new VirtualDPad(this);
            this.dPad.create();
        }

        this.boat = new BoatController(this, WORLD_WIDTH / 2, WORLD_HEIGHT / 2, () => this.dPad?.getDirection() ?? new Phaser.Math.Vector2(0, 0));
        this.weaponSystem = new WeaponSystem(this, this.boat.sprite);
        this.fishGroup = this.add.group();
        this.powerUpGroup = this.add.group();

        this.fishSpawner = new FishSpawner({
            scene: this,
            fishGroup: this.fishGroup,
            targetCount: TARGET_FISH_COUNT,
            worldWidth: WORLD_WIDTH,
            worldHeight: WORLD_HEIGHT,
            player: this.boat.sprite,
            speciesWeightMultipliers: this.currentLake.fishWeightMultipliers,
        });
        this.powerUpSpawner = new PowerUpSpawner({
            scene: this,
            group: this.powerUpGroup,
            worldWidth: WORLD_WIDTH,
            worldHeight: WORLD_HEIGHT,
        });

        this.fishSpawner.seed();

        this.physics.add.overlap(this.weaponSystem.getProjectileGroup(), this.fishGroup, (projectile, fish) => {
            const fishSprite = fish as FishBase;
            this.handleFishCaught(fishSprite);
            const projectileSprite = projectile as Phaser.Physics.Arcade.Image;
            projectileSprite.setActive(false);
            projectileSprite.setVisible(false);
            (projectileSprite.body as Phaser.Physics.Arcade.Body | null)?.setVelocity(0, 0);
        });

        this.physics.add.overlap(this.boat.sprite, this.powerUpGroup, (_boat, powerUp) => {
            const collectible = powerUp as PowerUpBase;
            this.applyPowerUp(collectible.powerUpType);
            this.audioManager.play("powerup");
            this.powerUpGroup.remove(collectible, true, true);
        });

        this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
            if (this.dPad && (this.dPad.ownsPointer(pointer) || this.dPad.isInControlZone(pointer.x, pointer.y))) {
                return;
            }

            const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
            const targetFish = this.findFishAt(worldPoint.x, worldPoint.y);
            if (targetFish) {
                const didThrow = this.weaponSystem.tryThrowAt(targetFish);
                if (didThrow) {
                    this.audioManager.play("throw");
                }
            }
        });

        this.cameras.main.startFollow(this.boat.sprite, true, 0.1, 0.1);
        this.cameras.main.setZoom(1);

        this.createHud();

        this.miniMap = new MiniMap({
            scene: this,
            x: 20,
            y: REFERENCE_HEIGHT - 180,
            width: 250,
            height: 150,
            worldWidth: WORLD_WIDTH,
            worldHeight: WORLD_HEIGHT,
        });
    }

    update(_time: number, delta: number): void {
        if (this.runEnded) {
            return;
        }

        if (this.gasTank.isEmpty()) {
            this.endRun();
            return;
        }

        const moving = this.boat.update();
        this.weaponSystem.update();
        this.gasTank.update(delta, moving, this.currentLake.gasDrainMultiplier);
        this.updateTimedEffects();

        const pointer = this.input.activePointer;
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        this.weaponSystem.updateFishHoverFeedback(worldPoint.x, worldPoint.y, this.fishGroup);

        this.fishGroup.getChildren().forEach((child) => {
            const fish = child as FishBase;
            fish.update(this.time.now);
            this.fishSpawner.wrapOrCull(fish);
        });

        if (this.fishGroup.getLength() === 0) {
            this.endRun();
            return;
        }

        this.fishSpawner.update();
        this.powerUpSpawner.update(this.time.now);

        this.updateHud();
        this.miniMap.update(this.boat.sprite, this.fishGroup, this.powerUpGroup);
        this.updatePerformanceHud(delta);
    }

    private createHud(): void {
        this.scoreText = this.add
            .text(REFERENCE_WIDTH / 2, 20, "Score: 0", {
                fontSize: "44px",
                color: "#ffffff",
            })
            .setOrigin(0.5, 0)
            .setScrollFactor(0)
            .setDepth(100);

        this.fishCountText = this.add
            .text(REFERENCE_WIDTH / 2, 74, "🐟 x 0", {
                fontSize: "32px",
                color: "#e9f7ff",
            })
            .setOrigin(0.5, 0)
            .setScrollFactor(0)
            .setDepth(100);

        this.gasText = this.add
            .text(20, 20, "Gas: 100%", {
                fontSize: "38px",
                color: "#77f29b",
            })
            .setScrollFactor(0)
            .setDepth(100);

        this.powerUpText = this.add
            .text(REFERENCE_WIDTH - 20, 20, "Weapon: Spear", {
                fontSize: "34px",
                color: "#d5ebff",
                align: "right",
            })
            .setOrigin(1, 0)
            .setScrollFactor(0)
            .setDepth(100);

        this.lakeInfoText = this.add
            .text(REFERENCE_WIDTH / 2, 114, `${this.currentLake.name} • ${this.currentSeason}`, {
                fontSize: "28px",
                color: "#d0efff",
            })
            .setOrigin(0.5, 0)
            .setScrollFactor(0)
            .setDepth(100);

        this.fpsText = this.add
            .text(REFERENCE_WIDTH - 20, 98, "FPS: --", {
                fontSize: "24px",
                color: "#c8e8ff",
            })
            .setOrigin(1, 0)
            .setScrollFactor(0)
            .setDepth(100);
    }

    private updateHud(): void {
        const safeScore = Number.isFinite(this.score) ? this.score : 0;
        this.scoreText.setText(`Score: ${safeScore}`);
        this.fishCountText.setText(`🐟 x ${this.fishCaught}`);
        const gas = this.gasTank.getCurrentPercent();
        this.gasText.setText(`Gas: ${gas.toFixed(0)}%`);
        this.powerUpText.setText(this.buildPowerUpStatusText());

        if (gas > 50) {
            this.gasText.setColor("#77f29b");
        } else if (gas > 25) {
            this.gasText.setColor("#f7db72");
        } else {
            this.gasText.setColor("#ff6b6b");
        }
    }

    private handleFishCaught(fish: FishBase): void {
        const points = Number.isFinite(fish.pointValue) ? fish.pointValue : 0;
        const baseScore = Number.isFinite(this.score) ? this.score : 0;
        this.score = baseScore + points;
        this.fishCaught += 1;
        ScorePopup.spawn(this, fish.x, fish.y - 12, points);
        if (points >= 50) {
            this.cameras.main.shake(130, 0.003);
            this.audioManager.play("bigCatch");
        } else {
            this.audioManager.play("catch");
        }
        this.fishGroup.remove(fish, true, true);

        if (this.fishGroup.getLength() === 0) {
            this.endRun();
        }
    }

    private endRun(): void {
        if (this.runEnded) {
            return;
        }

        this.runEnded = true;
        this.boat.sprite.setAcceleration(0, 0);
        this.boat.sprite.setVelocity(0, 0);
        this.audioManager.play("gameOver");
        this.scene.start("GameOverScene", {
            score: this.score,
            fishCount: this.fishCaught,
            lakeKey: this.currentLake.key,
            season: this.currentSeason,
        });
    }

    private findFishAt(worldX: number, worldY: number): FishBase | null {
        const fishChildren = this.fishGroup.getChildren() as FishBase[];
        const cameraView = this.cameras.main.worldView;
        return fishChildren.find((fish) => fish.active && fish.visible && cameraView.contains(fish.x, fish.y) && fish.getBounds().contains(worldX, worldY)) ?? null;
    }

    private applyPowerUp(type: PowerUpType): void {
        switch (type) {
            case PowerUpType.Trident:
                this.weaponSystem.applyWeaponPowerUp(WeaponType.Trident);
                break;
            case PowerUpType.Bow:
                this.weaponSystem.applyWeaponPowerUp(WeaponType.Bow);
                break;
            case PowerUpType.GasCan:
                this.gasTank.refill(25);
                break;
            case PowerUpType.SpeedBoost:
                this.speedBoostUntil = this.time.now + SPEED_BOOST_DURATION_MS;
                this.boat.setSpeedMultiplier(SPEED_BOOST_MULTIPLIER);
                break;
            case PowerUpType.FishFrenzy:
                this.fishFrenzyUntil = this.time.now + FISH_FRENZY_DURATION_MS;
                this.fishSpawner.setFrenzyMultiplier(FISH_FRENZY_MULTIPLIER);
                break;
        }
    }

    private updateTimedEffects(): void {
        if (this.speedBoostUntil > 0 && this.time.now >= this.speedBoostUntil) {
            this.speedBoostUntil = 0;
            this.boat.setSpeedMultiplier(1);
        }

        if (this.fishFrenzyUntil > 0 && this.time.now >= this.fishFrenzyUntil) {
            this.fishFrenzyUntil = 0;
            this.fishSpawner.setFrenzyMultiplier(1);
        }
    }

    private buildPowerUpStatusText(): string {
        const weapon = this.weaponSystem.getActiveWeapon();
        const weaponTimer = Math.ceil(this.weaponSystem.getRemainingWeaponMs() / 1000);
        const speedTimer = Math.max(0, Math.ceil((this.speedBoostUntil - this.time.now) / 1000));
        const frenzyTimer = Math.max(0, Math.ceil((this.fishFrenzyUntil - this.time.now) / 1000));
        const lines = [`Weapon: ${weapon}${weapon === WeaponType.Spear ? "" : ` (${weaponTimer}s)`}`];
        if (speedTimer > 0) {
            lines.push(`Speed Boost: ${speedTimer}s`);
        }
        if (frenzyTimer > 0) {
            lines.push(`Fish Frenzy: ${frenzyTimer}s`);
        }
        return lines.join("\n");
    }

    private updatePerformanceHud(delta: number): void {
        const instantFps = 1000 / Math.max(1, delta);
        this.fpsSampleTimer += delta;
        this.fpsAccumulated += instantFps;
        this.fpsFrames += 1;

        if (this.fpsSampleTimer < 500) {
            return;
        }

        const averageFps = this.fpsAccumulated / Math.max(1, this.fpsFrames);
        this.fpsText.setText(`FPS: ${averageFps.toFixed(0)}`);
        if (averageFps >= 50) {
            this.fpsText.setColor("#9df0a6");
        } else if (averageFps >= 30) {
            this.fpsText.setColor("#f4db8a");
        } else {
            this.fpsText.setColor("#ff9696");
        }

        this.fpsSampleTimer = 0;
        this.fpsAccumulated = 0;
        this.fpsFrames = 0;
    }

    private createTextures(): void {
        if (!this.textures.exists("boat")) {
            const boatGraphic = this.add.graphics();
            boatGraphic.fillStyle(0x8b5a2b, 1);
            boatGraphic.fillEllipse(28, 18, 56, 28);
            boatGraphic.fillStyle(0xf2a65a, 1);
            boatGraphic.fillCircle(28, 12, 7);
            boatGraphic.generateTexture("boat", 56, 36);
            boatGraphic.destroy();
        }

        if (!this.textures.exists("projectile")) {
            const projectileGraphic = this.add.graphics();
            projectileGraphic.fillStyle(0xf4f0e8, 1);
            projectileGraphic.fillCircle(6, 6, 6);
            projectileGraphic.generateTexture("projectile", 12, 12);
            projectileGraphic.destroy();
        }
    }
}
